import { useMutation, useQuery } from "@apollo/client";
import { Button, Grid, InputAdornment, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormTextField } from "components/Forms/FormTextField";
import { InfoBox } from "components/InfoBox";
import { gql } from "generated-graphql";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useWasteLinesUnified } from "hooks/useWasteLinesUnified";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";
import { useManifestInputValidation } from "../validationHooks/useManifestInputValidation";
import { DeleteManifestDialog } from "./DeleteManifestDialog";
import { RevertManifestDialog } from "./RevertManifestDialog";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];

const EDIT_VENDOR_MANIFEST = gql(`
  mutation EditVendorManifest($id: ID!, $input: ManifestEditInput!) {
    editVendorManifest(id: $id, input: $input) {
      originalProcessedManifest {
        id
        manifestTrackingNumber
        shippedDate
        vendor
        desFacilityName
        vendorManifest {
          manifestTrackingNumber
          shippedDate
          vendorSource
          desFacilityName
          manuallyEdited
        }
      }
      newProcessedManifest {
        id
        manifestTrackingNumber
        shippedDate
        vendor
        desFacilityName
        vendorManifest {
          manifestTrackingNumber
          shippedDate
          vendorSource
          desFacilityName
          manuallyEdited
        }
      }
    }
  }
`);

const WASTE_MANIFEST_VENDORS = gql(`
  query WasteManifestVendors {
    wasteManifestVendors
  }
`);

interface ManifestFormData {
  manifestTrackingNumber: string;
  shippedDate: string;
  vendorSource: string;
  desFacilityName: string;
}

interface EditManifestDialogProps {
  onClose: () => void;
  manifest: Manifest;
  onDeleteSuccess?: () => void;
}

export const EditManifestDialog = ({
  onClose,
  manifest,
  onDeleteSuccess,
}: EditManifestDialogProps) => {
  const { isStaff } = useCurrentUser();
  const { tenantId, tenant } = useTenant();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: wasteLinesUnified } = useWasteLinesUnified(
    {
      search: `tenantId:${tenantId} processedManifestId:${manifest.id}`,
    },
    { skip: !tenantId || !manifest.id }
  );

  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showRevertConfirmDialog, setShowRevertConfirmDialog] = useState(false);

  const { data: vendorsData } = useQuery<{ wasteManifestVendors: string[] }>(
    WASTE_MANIFEST_VENDORS
  );

  const vendorAutocompleteItems = useMemo(() => {
    const base = (vendorsData?.wasteManifestVendors ?? [])
      .map((v) => v?.trim())
      .filter((v): v is string => Boolean(v && v.length > 0));
    if (tenant?.name) base.unshift(tenant.name);
    const unique = Array.from(new Set(base));
    return unique.map((v) => ({ value: v, label: v }));
  }, [vendorsData?.wasteManifestVendors, tenant?.name]);

  const [updateVendorManifest, { loading: updating }] = useMutation(
    EDIT_VENDOR_MANIFEST,
    {
      onCompleted: (data) => {
        const newManifest = data.editVendorManifest.newProcessedManifest;
        if (newManifest) {
          alerts.success(
            `Manifest successfully updated. A new manifest has been created for manifest tracking number ${newManifest.manifestTrackingNumber}.`
          );
          // Replace the current manifest ID in the URL with the new manifest ID
          const pathSegments = location.pathname.split("/").filter(Boolean);
          const manifestIndex = pathSegments.findIndex(
            (segment) => segment === manifest.id
          );
          if (manifestIndex !== -1) {
            pathSegments[manifestIndex] = newManifest.id;
            const newPath = `/${pathSegments.join("/")}${location.search}`;
            navigate(newPath, { replace: true, preventScrollReset: true });
          }
        } else {
          alerts.success("Manifest successfully updated.");
        }
        onClose();
      },
      onError: (error) => {
        console.error("Failed to update manifest:", error);

        // Check if this is a duplicate manifest error
        const isDuplicateError = error.graphQLErrors?.some(
          (err) => err.extensions?.code === "DUPLICATE_MANIFEST"
        );

        if (isDuplicateError) {
          // Display the specific error message from the server
          const duplicateError = error.graphQLErrors.find(
            (err) => err.extensions?.code === "DUPLICATE_MANIFEST"
          );
          alerts.error(
            duplicateError?.message ||
              "A manifest with this tracking number already exists"
          );
        } else {
          // Generic error message for other types of errors
          alerts.error("Failed to update manifest. Please try again.");
        }
      },
    }
  );

  const defaultValues = useMemo<ManifestFormData>(
    () => ({
      manifestTrackingNumber:
        manifest.vendorManifest?.manifestTrackingNumber || "",
      shippedDate: manifest.vendorManifest?.shippedDate
        ? DateTime.fromISO(manifest.vendorManifest?.shippedDate, {
            zone: "utc",
          }).toISODate()
        : "",
      vendorSource: manifest.vendorManifest?.vendorSource || "",
      desFacilityName: manifest.vendorManifest?.desFacilityName || "",
    }),
    [manifest]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    issues,
  } = useValidatingForm<ManifestFormData>(
    defaultValues,
    [],
    useManifestInputValidation(manifest.id)
  );

  const onSubmit = (data: ManifestFormData) => {
    updateVendorManifest({
      variables: {
        id: manifest.id,
        input: {
          manifestTrackingNumber: data.manifestTrackingNumber,
          shippedDate: data.shippedDate
            ? DateTime.fromISO(data.shippedDate, { zone: "utc" })
                .startOf("day")
                .toISO()
            : null,
          vendorSource: data.vendorSource,
          desFacilityName: data.desFacilityName,
        },
      },
    });
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        title="Edit Manifest Info"
        titleBarActionElement={
          isStaff &&
          (manifest.vendorManifest?.manuallyEdited ||
            wasteLinesUnified?.wasteLinesUnified?.items.some(
              (l) => l.manuallyEdited
            )) ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="caption"
                color="textDisabled"
                fontStyle="italic"
              >
                edited
              </Typography>
              <Button
                color="error"
                onClick={() => setShowRevertConfirmDialog(true)}
              >
                Revert
              </Button>
            </Stack>
          ) : null
        }
        primaryActionButtonProps={{
          disabled: issues.length > 0 || updating || !isDirty,
          onClick: handleSubmit(onSubmit),
        }}
        secondaryActionElements={[
          <Button
            key="delete"
            color="error"
            onClick={() => setShowDeleteConfirmDialog(true)}
          >
            Delete Manifest
          </Button>,
        ]}
      >
        <Typography variant="body1" color="textSecondary">
          Editing this manifest will affect all associated line items and will
          only update vendor data.
        </Typography>
        <Grid container spacing={3}>
          <Grid size={6}>
            <InfoBox text={manifest.facility?.name} label="Facility Name" />
          </Grid>
          <Grid size={6}>
            <InfoBox
              text={
                manifest.facility
                  ? `${manifest.facility.streetAddress1} ${manifest.facility.city}, ${manifest.facility.state} ${manifest.facility.zip}`
                  : ""
              }
              label="Facility Address"
            />
          </Grid>
          <Grid size={12}>
            <InfoBox text={manifest.epaId} label="EPA ID" />
          </Grid>
          <Grid size={6}>
            <FormTextField
              name="manifestTrackingNumber"
              label="Manifest #"
              InputProps={{
                endAdornment:
                  !!manifest.vendor &&
                  manifest.vendorManifest?.originalManifestTrackingNumber !=
                    null &&
                  manifest.vendorManifest?.manifestTrackingNumber !==
                    manifest.vendorManifest?.originalManifestTrackingNumber ? (
                    <EditedInputAdornment />
                  ) : null,
              }}
              control={control}
            />
          </Grid>
          <Grid size={6}>
            <DateField
              name="shippedDate"
              label="Ship Date"
              control={control}
              endAdornment={
                !!manifest.vendor &&
                manifest.vendorManifest?.originalShippedDate != undefined &&
                manifest.vendorManifest?.shippedDate !==
                  manifest.vendorManifest?.originalShippedDate ? (
                  <EditedInputAdornment />
                ) : null
              }
            />
          </Grid>
          <Grid size={6}>
            <FormAutocomplete
              name="vendorSource"
              label="Vendor"
              control={control}
              InputProps={{
                endAdornment:
                  !!manifest.vendor &&
                  manifest.vendorManifest?.originalVendorSource != undefined &&
                  manifest.vendorManifest?.vendorSource !==
                    manifest.vendorManifest?.originalVendorSource ? (
                    <EditedInputAdornment />
                  ) : null,
              }}
              autocompleteItems={vendorAutocompleteItems}
              autocompleteProps={{
                freeSolo: true,
              }}
            />
          </Grid>
          <Grid size={6}>
            <FormTextField
              name="desFacilityName"
              label="TSDF"
              InputProps={{
                endAdornment:
                  !!manifest.vendor &&
                  manifest.vendorManifest?.originalDesFacilityName !=
                    undefined &&
                  manifest.vendorManifest?.desFacilityName !==
                    manifest.vendorManifest?.originalDesFacilityName ? (
                    <EditedInputAdornment />
                  ) : null,
              }}
              control={control}
            />
          </Grid>
        </Grid>
      </Dialog>
      {showDeleteConfirmDialog && (
        <DeleteManifestDialog
          onClose={() => setShowDeleteConfirmDialog(false)}
          manifest={manifest}
          onDeleteSuccess={onDeleteSuccess}
        />
      )}
      {showRevertConfirmDialog && (
        <RevertManifestDialog
          manifest={manifest}
          onClose={() => setShowRevertConfirmDialog(false)}
        />
      )}
    </>
  );
};

function EditedInputAdornment() {
  const { isStaff } = useCurrentUser();

  return isStaff ? (
    <InputAdornment position="end">
      <Typography variant="caption" fontStyle="italic" color="text.disabled">
        edited
      </Typography>
    </InputAdornment>
  ) : null;
}
